import React, { useCallback, useEffect, useState } from "react";
import { Store, RefreshCw, Save, Factory } from "lucide-react";
import { toast } from "sonner";
import { api, imageUrl } from "../lib/api";

const PLACEHOLDER = "https://customer-assets.emergentagent.com/job_store-hub-157/artifacts/yyc18991_IMG_4514.jpeg";

export function FactoryStockInput({ product, onSaved }) {
  const [val, setVal] = useState(product.factory_stock ?? 0);
  const [busy, setBusy] = useState(false);
  useEffect(() => { setVal(product.factory_stock ?? 0); }, [product.factory_stock]);
  const dirty = parseInt(val) !== (product.factory_stock ?? 0);
  const save = async (e) => {
    e?.stopPropagation?.();
    const n = Math.max(0, parseInt(val) || 0);
    setBusy(true);
    try {
      await api.patch(`/admin/catalog/${product.id}/factory-stock`, { factory_stock: n });
      toast.success(`Stock usine « ${product.name} » → ${n} (boutique nationale à jour)`);
      onSaved?.(product.id, n);
    } catch (err) { toast.error(err?.response?.data?.detail || "Erreur"); }
    finally { setBusy(false); }
  };
  return (
    <form onSubmit={(e) => { e.preventDefault(); save(e); }} onClick={(e) => e.stopPropagation()} className="flex items-center gap-1.5 shrink-0" data-testid="factory-stock-form">
      <label className="text-[10px] uppercase font-bold text-zinc-500 hidden sm:block">Usine</label>
      <input
        data-testid="factory-stock-input"
        type="number" min="0" inputMode="numeric" value={val}
        onChange={(e) => setVal(e.target.value)}
        className={`w-20 bg-ink border text-white px-2 py-1.5 text-center font-anton focus:outline-none ${dirty ? "border-neon-magenta" : "border-zinc-700"}`}
      />
      <button type="submit" data-testid="factory-stock-save" disabled={busy || !dirty} title="Enregistrer le stock usine" className="h-9 w-9 flex items-center justify-center bg-neon-green text-black disabled:opacity-30 hover:bg-white transition-colors"><Save className="h-4 w-4" /></button>
    </form>
  );
}

export function VendorStockAccordion() {
  const [data, setData] = useState([]);
  const [open, setOpen] = useState("");
  const [updated, setUpdated] = useState(null);
  const load = useCallback(() => api.get("/admin/stock/by-vendor").then((r) => { setData(r.data); setUpdated(new Date()); }).catch(console.error), []);
  useEffect(() => { load(); const id = setInterval(load, 30000); return () => clearInterval(id); }, [load]);

  return (
    <div className="bg-ink-surface border border-zinc-800 p-4" data-testid="vendor-stock-accordion">
      <div className="flex items-center gap-3 mb-3 flex-wrap">
        <h3 className="font-anton text-lg uppercase text-white">Stock en temps réel par boutique</h3>
        <span className="text-zinc-600 text-[11px]">{updated ? `mis à jour ${updated.toLocaleTimeString("fr-FR")} · auto toutes les 30 s` : "chargement…"}</span>
        <button data-testid="vendor-stock-refresh" onClick={load} className="ml-auto inline-flex items-center gap-1.5 text-xs uppercase font-bold border border-zinc-700 text-zinc-300 px-3 py-1.5 hover:border-neon-green hover:text-neon-green transition-colors"><RefreshCw className="h-3.5 w-3.5" /> Actualiser</button>
      </div>
      <div className="space-y-2">
        {data.map((v) => {
          const isOpen = open === v.vendor_id;
          return (
            <div key={v.vendor_id} className={`border ${isOpen ? "border-neon-green/60" : "border-zinc-800"}`} data-testid="vendor-stock-group">
              <button data-testid="vendor-stock-toggle" onClick={() => setOpen(isOpen ? "" : v.vendor_id)} className="w-full flex items-center gap-3 px-3 py-2.5 text-left">
                {v.is_national ? <Factory className="h-4 w-4 text-neon-magenta shrink-0" /> : <Store className="h-4 w-4 text-neon-green shrink-0" />}
                <span className="font-anton uppercase text-white">{v.name}</span>
                <span className="text-zinc-500 text-xs">{v.city}</span>
                {!v.is_national && <span className={`text-[10px] uppercase font-bold px-1.5 py-0.5 ${v.is_live ? "bg-neon-green text-black" : "border border-zinc-700 text-zinc-500"}`}>{v.is_live ? "En ligne" : "Hors ligne"}</span>}
                <span className="ml-auto text-xs text-zinc-400" data-testid="vendor-stock-total"><span className="text-white font-anton">{v.total_units}</span> u. · {v.items.length} réf.{v.out_of_stock > 0 && <span className="text-red-400 font-bold"> · {v.out_of_stock} rupture{v.out_of_stock > 1 ? "s" : ""}</span>}</span>
                <span className="text-zinc-500 text-xs">{isOpen ? "▲" : "▼"}</span>
              </button>
              {isOpen && (
                <div className="border-t border-zinc-800 max-h-96 overflow-y-auto" data-testid="vendor-stock-items">
                  {v.items.length === 0 ? <p className="text-zinc-600 text-xs p-3">Aucun produit en stock pour cette boutique.</p> : v.items.map((i) => (
                    <div key={i.catalog_id} className="flex items-center gap-3 px-3 py-1.5 border-b border-zinc-900 last:border-0 text-sm" data-testid="vendor-stock-item">
                      <img src={imageUrl(i.image_url) || PLACEHOLDER} alt="" className="h-8 w-8 object-cover" onError={(e) => { e.target.src = PLACEHOLDER; }} />
                      <span className="text-zinc-300 truncate">{i.name}</span>
                      <span className="text-zinc-600 text-xs hidden sm:inline">{i.category}</span>
                      <span className={`ml-auto font-anton text-base ${i.quantity <= 0 ? "text-red-500" : i.quantity <= 5 ? "text-orange-400" : "text-neon-green"}`}>{i.quantity}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
